import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Plus, Edit2, Trash2, X, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeObservations = [
  { value: 'dysfonctionnement', label: 'Dysfonctionnement récurrent', color: 'bg-red-100 text-red-700' },
  { value: 'alerte', label: 'Alerte technique', color: 'bg-orange-100 text-orange-700' },
  { value: 'preconisation', label: 'Préconisation de travaux', color: 'bg-blue-100 text-blue-700' },
  { value: 'anticipation', label: 'Anticipation grosses réparations', color: 'bg-purple-100 text-purple-700' },
  { value: 'amelioration', label: 'Amélioration suggérée', color: 'bg-green-100 text-green-700' },
];

const priorites = [
  { value: 'urgente', label: 'Urgente', color: 'bg-red-500' },
  { value: 'haute', label: 'Haute', color: 'bg-orange-500' },
  { value: 'moyenne', label: 'Moyenne', color: 'bg-yellow-500' },
  { value: 'basse', label: 'Basse', color: 'bg-gray-400' },
];

const statuts = [
  { value: 'ouvert', label: 'Ouvert', icon: AlertCircle },
  { value: 'en_cours', label: 'En cours', icon: Clock },
  { value: 'resolu', label: 'Résolu', icon: CheckCircle },
];

export default function ObservationsTab({ buildingId, token }) {
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  const fetchObservations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/carnet-entretien/observations/${buildingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setObservations(await response.json());
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, [buildingId, token]);

  useEffect(() => { fetchObservations(); }, [fetchObservations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingId ? `${API_URL}/api/carnet-entretien/observations/${editingId}` : `${API_URL}/api/carnet-entretien/observations`;
    const method = editingId ? 'PUT' : 'POST';
    try {
      const response = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, building_id: buildingId })
      });
      if (response.ok) {
        toast.success(editingId ? 'Observation modifiée' : 'Observation ajoutée');
        setShowModal(false); setEditingId(null); setForm({});
        fetchObservations();
      }
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette observation ?')) return;
    try {
      await fetch(`${API_URL}/api/carnet-entretien/observations/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Observation supprimée');
      fetchObservations();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleChangeStatut = async (id, newStatut) => {
    try {
      await fetch(`${API_URL}/api/carnet-entretien/observations/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: newStatut })
      });
      toast.success('Statut mis à jour');
      fetchObservations();
    } catch (error) { toast.error('Erreur'); }
  };

  const openNew = () => { setForm({ type_observation: 'preconisation', priorite: 'moyenne', statut: 'ouvert', date_constat: new Date().toISOString().split('T')[0] }); setEditingId(null); setShowModal(true); };
  const openEdit = (obs) => { setForm(obs); setEditingId(obs.id); setShowModal(true); };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  const filtered = observations.filter(o => {
    if (filterType && o.type_observation !== filterType) return false;
    if (filterStatut && o.statut !== filterStatut) return false;
    return true;
  });

  const countByStatut = (statut) => observations.filter(o => o.statut === statut).length;

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Observations & Préconisations</h2>
          <p className="text-sm text-gray-500">Suivi des alertes techniques et recommandations de travaux</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]" data-testid="add-observation-btn">
          <Plus className="h-5 w-5" />Ajouter
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="p-3 bg-red-100 rounded-lg">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{countByStatut('ouvert')}</p>
            <p className="text-sm text-gray-500">Ouvert(s)</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="p-3 bg-orange-100 rounded-lg">
            <Clock className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{countByStatut('en_cours')}</p>
            <p className="text-sm text-gray-500">En cours</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-lg">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{countByStatut('resolu')}</p>
            <p className="text-sm text-gray-500">Résolu(s)</p>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex gap-3">
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">Tous les types</option>
          {typeObservations.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">Tous les statuts</option>
          {statuts.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <AlertTriangle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Aucune observation enregistrée</p>
          <p className="text-sm text-gray-400 mt-1">Documentez les dysfonctionnements et préconisations pour le suivi technique</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(obs => {
            const typeObs = typeObservations.find(t => t.value === obs.type_observation) || typeObservations[0];
            const prio = priorites.find(p => p.value === obs.priorite);
            const stat = statuts.find(s => s.value === obs.statut);
            const StatIcon = stat?.icon || AlertCircle;
            
            return (
              <div key={obs.id} className={`bg-white rounded-xl border border-gray-200 p-5 ${obs.statut === 'resolu' ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeObs.color}`}>{typeObs.label}</span>
                      {prio && <span className={`w-2 h-2 rounded-full ${prio.color}`} title={prio.label}></span>}
                      <span className="text-xs text-gray-400">{formatDate(obs.date_constat)}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900">{obs.titre}</h3>
                    <p className="text-sm text-gray-600 mt-1">{obs.description}</p>
                    {obs.localisation && <p className="text-xs text-gray-400 mt-2">Localisation : {obs.localisation}</p>}
                    {obs.cout_estime && <p className="text-sm text-gray-600 mt-1">Coût estimé : {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(obs.cout_estime)}</p>}
                    {obs.action_recommandee && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800"><strong>Action recommandée :</strong> {obs.action_recommandee}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-1">
                      <StatIcon className={`h-4 w-4 ${obs.statut === 'resolu' ? 'text-green-500' : obs.statut === 'en_cours' ? 'text-orange-500' : 'text-red-500'}`} />
                      <select 
                        value={obs.statut} 
                        onChange={(e) => handleChangeStatut(obs.id, e.target.value)}
                        className="text-sm border-0 bg-transparent font-medium"
                      >
                        {statuts.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(obs)} className="p-1 hover:bg-gray-100 rounded"><Edit2 className="h-4 w-4 text-gray-400" /></button>
                      <button onClick={() => handleDelete(obs.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4 text-red-400" /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-semibold">{editingId ? 'Modifier' : 'Nouvelle'} observation</h3>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Type *</label>
                <select value={form.type_observation || ''} onChange={(e) => setForm({...form, type_observation: e.target.value})} required className="w-full px-3 py-2 border rounded-lg">
                  {typeObservations.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Titre *</label>
                <input type="text" value={form.titre || ''} onChange={(e) => setForm({...form, titre: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Fuite récurrente chaufferie" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <textarea value={form.description || ''} onChange={(e) => setForm({...form, description: e.target.value})} required rows="3" className="w-full px-3 py-2 border rounded-lg" placeholder="Détaillez le problème observé..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Date du constat</label>
                  <input type="date" value={form.date_constat || ''} onChange={(e) => setForm({...form, date_constat: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Priorité</label>
                  <select value={form.priorite || 'moyenne'} onChange={(e) => setForm({...form, priorite: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    {priorites.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Localisation</label>
                <input type="text" value={form.localisation || ''} onChange={(e) => setForm({...form, localisation: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Sous-sol, Hall A" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Action recommandée</label>
                <textarea value={form.action_recommandee || ''} onChange={(e) => setForm({...form, action_recommandee: e.target.value})} rows="2" className="w-full px-3 py-2 border rounded-lg" placeholder="Quelle action préconisez-vous ?" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Coût estimé (€)</label>
                <input type="number" value={form.cout_estime || ''} onChange={(e) => setForm({...form, cout_estime: parseFloat(e.target.value) || null})} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]">{editingId ? 'Modifier' : 'Ajouter'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
